import { LinkIcon, PenIcon, TrashIcon, UnlinkIcon } from "@xola/icons";
import { Badge, VerifoneImage } from "@xola/ui-kit";
import React, { useState } from "react";
import { EmvDevice, EmvDeviceType, Permission } from "types";
import { useSeller } from "../../../hooks/useAccount";
import { useStripeTerminal } from "../../../hooks/useStripeTerminal";
import { IsGranted } from "../../Common/IsGranted";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { getPairedDevice } from "../../Input/EmvInput";
import { PairDeviceModal } from "./PairDeviceModal";

interface DeviceProps {
    device: EmvDevice;
    canEdit?: boolean;
    onEdit?: (device: EmvDevice) => any;
    onDelete?: (device: EmvDevice) => any;
}

export const Device = ({ device, canEdit = true, onEdit, onDelete }: DeviceProps) => {
    const seller = useSeller();
    const [isOpenPairModal, setIsOpenPairModal] = useState(false);
    const togglePairModal = () => {
        setIsOpenPairModal(!isOpenPairModal);
    };

    const computer = JSON.parse(localStorage.getItem("computer") ?? "{}");
    const isPaired = !!(device?.computer?.id && device?.computer?.id === computer.id);
    const hasPairedDevice = !!getPairedDevice(seller);
    const cannotPair = hasPairedDevice && !isPaired; // The user has paired a device, but it isn't this one

    const terminal = useStripeTerminal();
    const connectedReader = terminal?.getConnectedReader();

    return (
        <div className="flex w-full rounded border border-gray-light py-2.5 px-4">
            <div className="flex-grow self-center">
                <div className="flex items-center space-x-4">
                    <DeviceImage device={device} />
                    <NoTranslate as="div">{device.label}</NoTranslate>
                    {isPaired && (
                        <Badge color="success" size="small">
                            Paired
                        </Badge>
                    )}
                    {device.remoteId === connectedReader?.id && (
                        <Badge color="primary" size="small">
                            Connected
                        </Badge>
                    )}
                </div>
            </div>
            {canEdit && (
                <div className="space-x-2">
                    {isPaired ? (
                        <SecondaryButton icon={<UnlinkIcon />} size="small" onClick={togglePairModal}>
                            Unpair
                        </SecondaryButton>
                    ) : (
                        <SecondaryButton
                            icon={<LinkIcon />}
                            size="small"
                            disabled={cannotPair}
                            onClick={togglePairModal}
                        >
                            Pair
                        </SecondaryButton>
                    )}
                    <IsGranted permissions={Permission.EDIT_PAIR_PAYMENT_DEVICE}>
                        <SecondaryButton className="align-bottom" size="small" onClick={() => onEdit?.(device)}>
                            <PenIcon />
                        </SecondaryButton>
                        <SecondaryButton className="align-bottom" size="small" onClick={() => onDelete?.(device)}>
                            <TrashIcon />
                        </SecondaryButton>
                    </IsGranted>
                </div>
            )}

            <PairDeviceModal
                device={device}
                isOpen={isOpenPairModal}
                onCancel={togglePairModal}
                onSubmit={togglePairModal}
            />
        </div>
    );
};

const DeviceImage = ({ device }: { device: EmvDevice }) => {
    // const defaultImage = <img width={25} height={25} alt={device.type} />;
    return device.type === EmvDeviceType.VERIFONE ? <VerifoneImage /> : null;
};
